/**
 * @component IconButton
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @spec Based on shadcn/ui + Figma design specification
 */

import { forwardRef, ReactNode, ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

export type IconButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'ghostMuted' | 'destructive';
export type IconButtonSize = 'mini' | 'small' | 'regular' | 'large';
export type IconButtonShape = 'default' | 'round';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  shape?: IconButtonShape;
  icon: ReactNode;
  'aria-label': string;
}

const iconButtonVariants = cva(
  'inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-accent-1 text-foreground hover:bg-accent-2',
        outline: 'border border-border bg-background text-foreground hover:bg-accent-0',
        ghost: 'text-foreground hover:bg-accent-1',
        ghostMuted: 'text-muted-foreground hover:bg-accent-1 hover:text-foreground',
        destructive: 'bg-danger text-danger-foreground hover:bg-danger/90',
      },
      size: {
        mini: 'h-6 w-6 [&>svg]:w-3 [&>svg]:h-3',
        small: 'h-8 w-8 [&>svg]:w-4 [&>svg]:h-4',
        regular: 'h-10 w-10 [&>svg]:w-5 [&>svg]:h-5',
        large: 'h-12 w-12 [&>svg]:w-6 [&>svg]:h-6',
      },
      shape: {
        default: 'rounded-md',
        round: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'regular',
      shape: 'default',
    },
  }
);

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, shape, icon, ...props }, ref) => {
    return (
      <button
        className={cn(iconButtonVariants({ variant, size, shape }), className)}
        ref={ref}
        {...props}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
